/**
 * Friendly fallback display names for accounts that never supplied a real name
 * (email-only signups since PANDA-1520 stopped collecting first/last upfront,
 * plus OAuth signups where the provider shared no name).
 *
 * The name is generated deterministically from a stable seed (the user's email)
 * so the SAME account always resolves to the SAME name — in emails, profile UI,
 * and analytics — across every session, with no database backfill required. New
 * signups also persist it into `user_metadata` so Settings and analytics read a
 * real value; existing accounts fall back to the deterministic result on the
 * fly. Two words, ChatGPT-style (e.g. `Amber Otter`), so it reads as a friendly
 * handle and never like a placeholder such as `there`, `null`, or `user1234`.
 */

// Warm, nature/colour adjectives — nothing that could read as negative or
// off-brand when paired with any noun below.
const ADJECTIVES = [
  "Amber",
  "Azure",
  "Coral",
  "Crimson",
  "Emerald",
  "Golden",
  "Hazel",
  "Indigo",
  "Ivory",
  "Jade",
  "Lunar",
  "Maple",
  "Misty",
  "Olive",
  "Opal",
  "Pearl",
  "Sage",
  "Scarlet",
  "Silver",
  "Solar",
  "Sunny",
  "Teal",
  "Velvet",
  "Violet",
];

// Friendly creatures / nature nouns — Panda included as an on-brand nod.
const NOUNS = [
  "Otter",
  "Falcon",
  "Willow",
  "Meadow",
  "Comet",
  "Sparrow",
  "Panda",
  "Heron",
  "Fox",
  "Lynx",
  "Koala",
  "Robin",
  "Finch",
  "Cedar",
  "Juniper",
  "Lotus",
  "Marlin",
  "Pebble",
  "Quokka",
  "Reef",
  "Summit",
  "Thistle",
  "Wren",
  "Aspen",
];

/** FNV-1a 32-bit hash → stable, well-distributed unsigned int for a seed. */
function hashSeed(seed: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < seed.length; i++) {
    h ^= seed.charCodeAt(i);
    // 32-bit FNV prime multiply, kept unsigned via >>> 0.
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h >>> 0;
}

/**
 * Deterministic friendly display name for a seed (use the user's email or id).
 * Same seed → same name, forever. Falls back to a fixed pair for a blank seed
 * so the return value is never empty.
 */
export function generateFallbackDisplayName(seed: string | null | undefined): string {
  const normalized = (seed ?? "").trim().toLowerCase();
  const h = normalized ? hashSeed(normalized) : 0;
  const adjective = ADJECTIVES[h % ADJECTIVES.length];
  // Divide out the adjective span before indexing nouns so the two words don't
  // move in lockstep across nearby seeds.
  const noun = NOUNS[Math.floor(h / ADJECTIVES.length) % NOUNS.length];
  return `${adjective} ${noun}`;
}
